import { useEffect, useRef, useState } from 'react';
import { AscDesc, Channel } from 'stream-chat';
import { Session } from '@bizzabo/entities';
import { useChatClient } from '@bizzabo/chat-core';
import { instanaReportError } from '@bizzabo/instana-api';
import { CHANNEL_TYPE } from '../consts/channel';
import { joinToChannels } from '../utils/joinChannels';

const useChannelsAsync = (
    accountId: number,
    eventId: number,
    session: Session,
    isJoinChannelAsync = false
) => {
    const [channels, setChannels] = useState<Channel[]>(null);
    const { client } = useChatClient();
    const joinChannelsInterval = useRef<NodeJS.Timeout>(null);

    const POLL_INTERVAL = 3000;

    const resetJoinChannelsInterval = () => {
        if (isJoinChannelAsync && joinChannelsInterval.current !== null) {
            clearInterval(joinChannelsInterval.current);
            joinChannelsInterval.current = null;
        }
    };

    const getChannelsFromGetStream = async sessionChannels => {
        const sort = [{ last_message_at: -1 as AscDesc }];
        const filter = {
            cid: {
                $in: sessionChannels.map(channel => `${CHANNEL_TYPE}:${channel.channelId}`),
            },
        };

        try {
            const channels = await client.queryChannels(filter, sort, {
                watch: true,
                state: true,
            });

            if (channels?.length === sessionChannels.length) {
                setChannels(channels);
                return true;
            }
            return false;
        } catch {
            return false;
        }
    };

    const queryChannel = async sessionChannels => {
        try {
            const isSucceed = await getChannelsFromGetStream(sessionChannels);
            if (isSucceed) {
                resetJoinChannelsInterval();
            }
        } catch (error) {
            instanaReportError({ error });
        }
    };

    const pollQueryChannels = sessionChannels => {
        let tries = 0;
        joinChannelsInterval.current = setInterval(async () => {
            try {
                await queryChannel(sessionChannels);
            } catch (error) {
                instanaReportError({ error });
            } finally {
                if (++tries > 5) {
                    resetJoinChannelsInterval();
                }
            }
        }, POLL_INTERVAL);
    };

    const joinChannelsFlow = async sessionChannels => {
        try {
            await joinToChannels(accountId, eventId, session);
            pollQueryChannels(sessionChannels);
        } catch (error) {
            instanaReportError({ error });
            resetJoinChannelsInterval();
        }
    };

    const initChannels = async () => {
        const sessionChannels = session?.chat?.channels;

        if (!client || !sessionChannels?.length) {
            return;
        }

        try {
            const isSucceed = await getChannelsFromGetStream(sessionChannels);
            if (!isSucceed) {
                joinChannelsFlow(sessionChannels);
            }
        } catch (error) {
            joinChannelsFlow(sessionChannels);
        }
    };

    useEffect(() => {
        if (isJoinChannelAsync) {
            initChannels();
        }
    }, [client, session]);

    useEffect(() => {
        return () => resetJoinChannelsInterval();
    }, []);

    return { channelsAsync: channels };
};

export default useChannelsAsync;
